import { useMemo, useState } from "react";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Input } from "../../../app/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { Separator } from "../../../app/components/ui/separator";
import { DealerSelector } from "../../../components/selectors/DealerSelector";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api } from "../../../lib/api";
import { trackEvent } from "../../../lib/tracking";
import { trackFirstInventoryAddedOnce } from "../../../lib/trackingMilestones";
import { useAuth } from "../../../providers/AuthProvider";

export type BulkValidateRow = Awaited<ReturnType<typeof api.inventory.bulkValidate>>["valid_rows"][number];
export type BulkInvalidRow = Awaited<ReturnType<typeof api.inventory.bulkValidate>>["invalid_rows"][number];

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "http://127.0.0.1:8000";

export function InventoryBulkUploadPage() {
  const { token } = useAuth();

  const [file, setFile] = useState<File | null>(null);
  const [dealerId, setDealerId] = useState<string>("");

  const [validRows, setValidRows] = useState<BulkValidateRow[]>([]);
  const [invalidRows, setInvalidRows] = useState<BulkInvalidRow[]>([]);

  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);

  const [rootError, setRootError] = useState<string | null>(null);
  const [commitSummary, setCommitSummary] = useState<{ successCount: number; failedCount: number } | null>(null);
  const [commitErrors, setCommitErrors] = useState<Array<{ row_number: number; reason: string }>>([]);

  const canCommit = useMemo(() => {
    return Boolean(token && dealerId && validRows.length > 0 && invalidRows.length === 0);
  }, [token, dealerId, validRows.length, invalidRows.length]);

  async function downloadSample() {
    if (!token) return;
    const res = await fetch(`${API_BASE_URL}/api/inventory/bulk/sample`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      throw new Error(`Failed to download sample (HTTP ${res.status})`);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inventory-bulk-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function onValidate() {
    if (!token) return;
    if (!file) {
      setRootError("Please select a CSV file.");
      return;
    }

    setRootError(null);
    setCommitSummary(null);
    setCommitErrors([]);
    setValidRows([]);
    setInvalidRows([]);

    setValidating(true);
    try {
      const res = await api.inventory.bulkValidate({ token, file });
      setValidRows(res.valid_rows);
      setInvalidRows(res.invalid_rows);
    } catch (e) {
      setRootError(e instanceof Error ? e.message : "Failed to validate CSV.");
    } finally {
      setValidating(false);
    }
  }

  async function onCommit() {
    if (!token) return;
    if (!canCommit) return;

    setRootError(null);
    setCommitSummary(null);
    setCommitErrors([]);

    setCommitting(true);
    try {
      const res = await api.inventory.bulkCommit({
        token,
        payload: {
          dealer_id: dealerId,
          rows: validRows.map((r) => ({
            row_number: r.row_number,
            is_new_product: r.is_new_product,
            product_id: r.product_id,
            sku: r.sku,
            product_name: r.product_name,
            brand: r.brand,
            categories: r.categories,
            quantity: r.quantity,
            purchase_price: r.purchase_price,
            selling_price: r.selling_price,
            mrp: r.mrp,
            expiry_date: r.expiry_date,
          })),
        },
      });

      setCommitSummary({ successCount: res.success_count, failedCount: res.failed_count });
      setCommitErrors(res.errors);
      if (res.success_count > 0) {
        trackEvent(TRACKING_EVENTS.INVENTORY_BULK_UPLOAD, {
          success_count: res.success_count,
          failed_count: res.failed_count,
        });
        trackFirstInventoryAddedOnce();
      }
    } catch (e) {
      setRootError(e instanceof Error ? e.message : "Failed to commit inventory upload.");
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-1">Bulk inventory upload</h1>
          <p className="text-muted-foreground">Upload a CSV, preview valid rows, then commit in chunks.</p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void downloadSample().catch((e) => setRootError(e instanceof Error ? e.message : "Failed to download sample."));
            }}
          >
            Download sample
          </Button>
        </div>
      </div>

      {rootError ? (
        <Alert variant="destructive">
          <AlertTitle>Upload failed</AlertTitle>
          <AlertDescription>{rootError}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>1) Upload & validate</CardTitle>
          <CardDescription>
            Required columns (alphabetically): brand, categories, cost_price, expiry_date, mrp, product_name, quantity,
            selling_price, sku. Unknown SKUs create products; brands and categories are created when missing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                }}
              />
            </div>
            <Button type="button" onClick={() => void onValidate()} disabled={!file || validating}>
              {validating ? "Validating..." : "Validate CSV"}
            </Button>
          </div>

          <Separator />

          <div className="text-sm text-muted-foreground">
            {validRows.length > 0 ? <div>Valid rows: {validRows.length}</div> : null}
            {invalidRows.length > 0 ? <div className="text-destructive">Invalid rows: {invalidRows.length}</div> : null}
          </div>
        </CardContent>
      </Card>

      {(validRows.length > 0 || invalidRows.length > 0) && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>2) Preview</CardTitle>
            <CardDescription>Only valid rows will be committed.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {validRows.length > 0 ? (
              <div className="space-y-2">
                <div className="font-medium">Valid rows</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>New</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Purchase</TableHead>
                      <TableHead>MRP</TableHead>
                      <TableHead>Selling</TableHead>
                      <TableHead>Expiry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validRows.map((r) => (
                      <TableRow key={r.row_number}>
                        <TableCell className="font-mono text-xs">{r.row_number}</TableCell>
                        <TableCell>{r.is_new_product ? "Yes" : "No"}</TableCell>
                        <TableCell className="font-mono text-xs">{r.sku}</TableCell>
                        <TableCell>{r.product_name}</TableCell>
                        <TableCell className="max-w-[140px] truncate" title={r.brand}>
                          {r.brand || "—"}
                        </TableCell>
                        <TableCell className="max-w-[180px] truncate" title={r.categories}>
                          {r.categories || "—"}
                        </TableCell>
                        <TableCell>{r.quantity}</TableCell>
                        <TableCell>{r.purchase_price}</TableCell>
                        <TableCell>{r.mrp}</TableCell>
                        <TableCell>{r.selling_price}</TableCell>
                        <TableCell>{r.expiry_date ?? "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}

            {invalidRows.length > 0 ? (
              <div className="space-y-2">
                <div className="font-medium text-destructive">Invalid rows</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invalidRows.map((r) => (
                      <TableRow key={r.row_number}>
                        <TableCell className="font-mono text-xs">{r.row_number}</TableCell>
                        <TableCell className="text-destructive">
                          <ul className="list-disc pl-5">
                            {r.errors.map((err, idx) => (
                              <li key={`${r.row_number}-${idx}`}>{err}</li>
                            ))}
                          </ul>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>3) Commit</CardTitle>
          <CardDescription>We will insert stock batches and `stock_ledgers` (IN) per chunk.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <div className="text-sm font-medium mb-1">Dealer</div>
              <DealerSelector value={dealerId} onValueChange={setDealerId} placeholder="Select dealer for this upload" />
            </div>

            <div>
              <Button type="button" disabled={!canCommit || committing} onClick={() => void onCommit()}>
                {committing ? "Committing..." : "Commit upload"}
              </Button>
            </div>
          </div>

          {commitSummary ? (
            <Alert>
              <AlertTitle>Commit summary</AlertTitle>
              <AlertDescription>
                Success: {commitSummary.successCount} rows. Failed: {commitSummary.failedCount} rows.
              </AlertDescription>
            </Alert>
          ) : null}

          {commitErrors.length > 0 ? (
            <div className="space-y-2">
              <div className="font-medium text-destructive">Commit errors</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commitErrors.map((e, idx) => (
                    <TableRow key={`${e.row_number}-${idx}`}>
                      <TableCell className="font-mono text-xs">{e.row_number}</TableCell>
                      <TableCell className="text-destructive">{e.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

